import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Filter } from 'lucide-react';
import PlayerSearchFilters from './PlayerSearchFilters';
import PlayerSearchResultItem from './PlayerSearchResultItem';
import { PlayerSearchProps } from './types';
import { Player as PlayerType } from "@/types/Player";

import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

import PlayerDialog from './PlayerDialog';

interface Player extends PlayerType {
  teamStatus?: string;
  updated_at: string;
  sex?: 'male' | 'female' | 'other';
  jersey_number?: string;
  age_group?: any;
  first_name: string;
  last_name: string;
  id: string;
}

const PlayerSearch = ({
  playerSearchTerm,
  setPlayerSearchTerm,
  handleSearch,
  isSearchingPlayers,
  searchResults,
  handleAddPlayerToConsideration,
  showFilters,
  setShowFilters,
  // Filter props passed down
  genderFilter, setGenderFilter,
  ageGroupFilter, setAgeGroupFilter,
  positionsFilter, handlePositionFilterChange,
  competitivenessFilter, handleCompetitivenessFilterChange,
  coachAgeGroups, loadingAgeGroups,
  positionsOptions, competitivenessOptions,
  clearFilters,
  currentTeamId,
}: PlayerSearchProps & { currentTeamId: string }) => {

  const showNoResultsMessage = searchResults.length === 0 &&
    (playerSearchTerm.trim() || genderFilter !== 'all' || ageGroupFilter !== 'all' || positionsFilter.length > 0 || competitivenessFilter.length > 0);

  const showInitialMessage = searchResults.length === 0 && !showNoResultsMessage;

  // --- Batch fetch average ratings ---
  const [avgRatings, setAvgRatings] = useState<Record<string, number | null>>({});
  const [loadingRatings, setLoadingRatings] = useState(false);

  // --- PlayerDialog state ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPlayerId, setDialogPlayerId] = useState<string | null>(null);
  const [dialogPlayerNotes, setDialogPlayerNotes] = useState('');

  // Helper: get player name for dialog
  const getDialogPlayerName = (): string => {
    const player = searchResults.find(p => p.id === dialogPlayerId);
    return player ? `${player.first_name || ''} ${player.last_name || ''}`.trim() : '';
  };

  // Handler to open dialog
  const handleViewDetails = (playerId: string) => {
    setDialogPlayerId(playerId);
    setDialogOpen(true);
    // Optionally load notes here if desired.
    setDialogPlayerNotes(''); // Or fetch from API if needed
  };

  // Handler to close dialog
  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setDialogPlayerId(null);
      setDialogPlayerNotes('');
    }
  };

  // Handler to save notes for the selected player and team
  const handleDialogSave = async () => {
    if (!dialogPlayerId || !currentTeamId) return;
    try {
      const { error } = await supabase
        .from('team_player_notes')
        .upsert({
          player_id: dialogPlayerId,
          team_id: currentTeamId,
          notes: dialogPlayerNotes,
          note_type: 'coach',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'player_id,team_id' });
      if (error) {
        console.error('Failed to save notes:', error.message);
        // Optionally show error to user
      }
    } catch (err) {
      console.error('Error saving notes:', err);
      // Optionally show error to user
    } finally {
      setDialogOpen(false);
      setDialogPlayerId(null);
      setDialogPlayerNotes('');
    }
  };

  const [sortField, setSortField] = useState<string>('average_rating');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Sort players based on current sort field and direction
  const sortedPlayers = useMemo(() => {
    if (!searchResults) return searchResults;
    
    return [...searchResults].sort((a, b) => {
      let aValue: string | number | undefined;
      let bValue: string | number | undefined;
      
      if (sortField === 'average_rating') {
        aValue = avgRatings[a.id] || 0;
        bValue = avgRatings[b.id] || 0;
      } else {
        aValue = (a as any)[sortField];
        bValue = (b as any)[sortField];
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      return 0;
    });
  }, [searchResults, sortField, sortDirection, avgRatings]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'average_rating' ? 'desc' : 'asc');
    }
  };

  useEffect(() => {
    async function fetchAvgRatings() {
      if (!searchResults || searchResults.length === 0) {
        setAvgRatings({});
        return;
      }
      setLoadingRatings(true);
      const playerIds = searchResults.map(p => p.id);
      // Supabase: SELECT player_id, AVG(rating_average) as avg_rating FROM player_evaluations WHERE player_id IN (...) GROUP BY player_id
      const { data, error } = await supabase
        .from('player_evaluations')
        .select('player_id, rating_average')
        .in('player_id', playerIds);
      if (error || !data) {
        setAvgRatings({});
        setLoadingRatings(false);
        return;
      }
      // Group by player_id and average
      const ratingsMap: Record<string, number[]> = {};
      data.forEach((row: any) => {
        if (!row.player_id) return;
        if (typeof row.rating_average !== 'number') return;
        if (!ratingsMap[row.player_id]) ratingsMap[row.player_id] = [];
        ratingsMap[row.player_id].push(row.rating_average);
      });
      const avgMap: Record<string, number | null> = {};
      playerIds.forEach(pid => {
        const arr = ratingsMap[pid];
        if (!arr || arr.length === 0) avgMap[pid] = null;
        else avgMap[pid] = arr.reduce((a, b) => a + b, 0) / arr.length;
      });
      setAvgRatings(avgMap);
      setLoadingRatings(false);
    }
    fetchAvgRatings();
  }, [searchResults]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Search className="mr-2 h-5 w-5" /> Player Search
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-grow">
            <Search className="absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by player name..."
              value={playerSearchTerm}
              onChange={(e) => setPlayerSearchTerm(e.target.value)}
              className="pl-9"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </div>
          <Button
            variant="default"
            size="sm" // Standardize size
            onClick={handleSearch}
            disabled={isSearchingPlayers}
            className="mr-2" // Keep margin consistent
          >
            {isSearchingPlayers ? 'Searching...' : 'Search'}
          </Button>
          <Button
            variant={showFilters ? "secondary" : "outline"}
            size="sm" // Standardize size
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <PlayerSearchFilters
            genderFilter={genderFilter}
            setGenderFilter={setGenderFilter}
            ageGroupFilter={ageGroupFilter}
            setAgeGroupFilter={setAgeGroupFilter}
            positionsFilter={positionsFilter}
            handlePositionFilterChange={handlePositionFilterChange}
            competitivenessFilter={competitivenessFilter}
            handleCompetitivenessFilterChange={handleCompetitivenessFilterChange}
            coachAgeGroups={coachAgeGroups}
            loadingAgeGroups={loadingAgeGroups}
            positionsOptions={positionsOptions}
            competitivenessOptions={competitivenessOptions}
            clearFilters={clearFilters}
            handleSearch={handleSearch}
            isSearchingPlayers={isSearchingPlayers}
          />
        )}

        {/* Player count */}
         <div className="mb-2 mt-4 text-sm text-gray-600 flex items-center justify-between">
            {searchResults.length > 0 && (
              <span className="font-medium">{searchResults.length} Player{searchResults.length !== 1 ? 's' : ''} Found</span>
            )}
            {/* Potential future sorting UI */}
         </div>

        {/* Search Results */}
        {isSearchingPlayers ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
          </div>
        ) : sortedPlayers && sortedPlayers.length > 0 ? (
          <div className="mt-1 max-h-[50vh] overflow-y-auto border rounded-md bg-white">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Player</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Birthdate & Age Group</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Rating</th>
                  <th className="hidden sm:table-cell py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {sortedPlayers.map((player: any) => (
                  <PlayerSearchResultItem
                    key={player.id}
                    player={player}
                    teamStatus={(player as any).teamStatus}
                    onAddPlayer={handleAddPlayerToConsideration}
                    avgRating={avgRatings[player.id]}
                    loadingRating={loadingRatings}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </tbody>
            </table>

            {/* PlayerDialog rendered at parent level for best UX */}
            <PlayerDialog
              isOpen={dialogOpen}
              onOpenChange={handleDialogOpenChange}
              currentPlayerId={dialogPlayerId}
              currentPlayerNotes={dialogPlayerNotes}
              setCurrentPlayerNotes={setDialogPlayerNotes}
              onSave={handleDialogSave}
              playerName={getDialogPlayerName()}
              currentTeamId={currentTeamId}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500 mt-4 text-center py-4 border rounded-md bg-gray-50">
             {showNoResultsMessage ?
                `No players found matching the current criteria or they are already associated with this team.` :
                showInitialMessage ?
                'Enter a name or use filters above, then click Search to find players.' : '' // Avoid showing message initially before any search/filter action
             }
           </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PlayerSearch;